import React, { useRef, useState, useEffect } from 'react';
import {
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
  FaDownload,
} from 'react-icons/fa';
import './VideoPlayer.css';

const VideoPlayer = ({ videoUrl, title, onClose, downloadUrl, fileName, resourceId }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const controlsTimeoutRef = useRef(null);

  // Format time as mm:ss
  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      if (!hasStarted) setHasStarted(true);
      setIsPlaying(!isPlaying);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVideoError = (e) => {
    const video = e.target;
    const code = video.error?.code;
    const msg = video.error?.message || 'Error desconocido';
    const errorMap = {
      1: 'Reproducción abortada por el usuario',
      2: 'Error de red al cargar el video',
      3: 'Error al decodificar el video (formato no soportado)',
      4: 'Formato de video no soportado o URL inválida',
    };
    const errorMsg = errorMap[code] || msg;
    console.error('❌ [VideoPlayer] Error code', code, ':', errorMsg, '| URL:', video.src);
    setVideoError(errorMsg);
  };

  // Reset error cuando cambia la URL del video
  useEffect(() => {
    setVideoError(null);
    setIsPlaying(false);
  }, [videoUrl]);

  const handleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current?.webkitRequestFullscreen) {
        containerRef.current.webkitRequestFullscreen(); // Safari
      }
      setIsFullscreen(true);
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (document.webkitFullscreenElement) {
        document.webkitExitFullscreen(); // Safari
      }
      setIsFullscreen(false);
    }
  };

  const handleFullscreenChange = () => {
    setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      clearTimeout(controlsTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        handleFullscreen();
      }
      if (e.key === ' ') {
        e.preventDefault();
        handlePlayPause();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isPlaying, isFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={containerRef}
      className="video-player"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => {
        if (isPlaying) setShowControls(false);
      }}
    >
      {/* Si es URL de video-translation-service (integrado), mostrar en iframe */}
      {videoUrl && videoUrl.includes('/api/vts') ? (
        <iframe
          src={videoUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: '8px',
          }}
          title={title || 'Video Player'}
          allowFullScreen
        />
      ) : (
        <>
          <video
            ref={videoRef}
            src={videoUrl}
            crossOrigin="anonymous"
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onError={handleVideoError}
          />

          {/* Error overlay */}
          {videoError && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.85)', color: '#ef4444',
              padding: '2rem', textAlign: 'center', gap: '1rem',
            }}>
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <p style={{ fontWeight: 600 }}>No se pudo reproducir el video</p>
              <p style={{ fontSize: '0.85rem', color: '#aaa' }}>{videoError}</p>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  download={fileName || 'archivo'}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1.25rem',
                    background: '#2563eb',
                    color: '#fff',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  ⬇ Descargar archivo
                </a>
              )}
            </div>
          )}

          {/* Controls */}
          <div className={`video-controls ${showControls ? 'visible' : 'hidden'}`}>
            {/* Progress Bar */}
            <div className="progress-bar-container">
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleProgressChange}
                className="progress-bar"
                style={{ '--progress': `${(currentTime / (duration || 1)) * 100}%` }}
              />
            </div>

            {/* Control Buttons */}
            <div className="controls-bottom">
              {/* Left Side: Play, Volume */}
              <div className="controls-left">
                <button
                  className="control-btn play-btn-circle"
                  onClick={handlePlayPause}
                  title={isPlaying ? 'Pausar' : 'Reproducir'}
                >
                  {isPlaying ? <FaPause /> : <FaPlay className="play-icon-triangle" />}
                </button>

                <div className="volume-control">
                  <button
                    className="control-btn"
                    onClick={() => {
                      const newVolume = volume === 0 ? 1 : 0;
                      setVolume(newVolume);
                      if (videoRef.current) {
                        videoRef.current.volume = newVolume;
                      }
                    }}
                    title={volume === 0 ? 'Activar sonido' : 'Silenciar'}
                  >
                    {volume === 0 ? <FaVolumeMute /> : <FaVolumeUp />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="volume-slider"
                    title="Volumen"
                  />
                </div>

                <div className="time-display">
                  <span>{formatTime(currentTime)}</span>
                  <span> / </span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Right Side: Fullscreen */}
              <div className="controls-right">
                {downloadUrl && (
                  <button
                    className={`control-btn ${!hasStarted ? 'disabled-control' : ''}`}
                    onClick={() => {
                      if (!hasStarted) return;
                      const a = document.createElement('a');
                      a.href = downloadUrl;
                      a.download = fileName || 'video';
                      a.target = '_blank';
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }}
                    disabled={!hasStarted}
                    title={hasStarted ? 'Descargar video' : 'Reproduce el video para habilitar descarga'}
                  >
                    <FaDownload />
                  </button>
                )}
                <button
                  className="control-btn"
                  onClick={handleFullscreen}
                  title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                >
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Close Button (visible always) */}
      <button className="close-btn" onClick={onClose} title="Cerrar">
        ✕
      </button>
    </div>
  );
};

export default VideoPlayer;
